/**
 * SKELETON LOADERS v2.0 - ULTRA OPTIMIZED
 * ✅ React.memo en TODOS los componentes
 * ✅ useMemo para configuraciones
 * ✅ Componentes de carga con shimmer effect
 */
import { memo, useMemo } from 'react';

// ═══════════════════════════════════════════════
// BASE SKELETON
// ═══════════════════════════════════════════════

export const Skeleton = memo(function Skeleton({ 
  width = 'w-full', 
  height = 'h-4', 
  rounded = 'rounded-lg', 
  className = '' 
}) {
  return (
    <div className={`${width} ${height} ${rounded} bg-gray-700/50 animate-shimmer ${className}`} />
  );
});

// ═══════════════════════════════════════════════
// SKELETON PRESETS
// ═══════════════════════════════════════════════

export const SkeletonCard = memo(function SkeletonCard() {
  return (
    <div className="bg-gray-800/40 border border-gray-700/50 rounded-xl p-4 animate-fadeIn">
      <div className="flex items-start gap-3 mb-3">
        <Skeleton width="w-10" height="h-10" rounded="rounded-lg" />
        <div className="flex-1">
          <Skeleton width="w-3/4" height="h-4" className="mb-2" />
          <Skeleton width="w-1/2" height="h-3" />
        </div>
      </div>
      <Skeleton width="w-full" height="h-20" />
    </div>
  );
});

export const SkeletonList = memo(function SkeletonList({ items = 3 }) {
  const skeletonItems = useMemo(() => 
    Array.from({ length: items }).map((_, i) => i)
  , [items]);

  return (
    <div className="space-y-3">
      {skeletonItems.map((i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
});

export const SkeletonTable = memo(function SkeletonTable({ rows = 5, cols = 4 }) {
  const tableRows = useMemo(() => 
    Array.from({ length: rows }).map((_, i) => i)
  , [rows]);

  const tableCols = useMemo(() => 
    Array.from({ length: cols }).map((_, i) => i)
  , [cols]);

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
      <div className="bg-gray-700/50 p-4 border-b border-gray-700 flex gap-4">
        {tableCols.map((i) => (
          <Skeleton key={i} width="flex-1" height="h-4" />
        ))}
      </div>
      {tableRows.map((i) => (
        <div key={i} className="p-4 border-b border-gray-700/30 flex gap-4">
          {tableCols.map((j) => (
            <Skeleton key={j} width="flex-1" height="h-3" />
          ))}
        </div>
      ))}
    </div>
  );
});

// ═══════════════════════════════════════════════
// SKELETON ESPECÍFICOS POR PÁGINA
// ═══════════════════════════════════════════════

export const ComponentsPageSkeleton = memo(function ComponentsPageSkeleton() {
  const categoryButtons = useMemo(() => [1, 2, 3, 4, 5], []);

  return (
    <div className="max-w-4xl mx-auto pb-24 px-4">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Skeleton width="w-12" height="h-12" rounded="rounded-xl" />
            <div>
              <Skeleton width="w-40" height="h-6" className="mb-1" />
              <Skeleton width="w-32" height="h-3" />
            </div>
          </div>
          <Skeleton width="w-10" height="h-10" rounded="rounded-xl" />
        </div>
        <Skeleton width="w-full" height="h-10" rounded="rounded-xl" className="mb-3" />
        <div className="flex gap-2 overflow-x-auto">
          {categoryButtons.map(i => (
            <Skeleton key={i} width="w-24" height="h-8" rounded="rounded-lg" />
          ))}
        </div>
      </div>
      <SkeletonList items={4} />
    </div>
  );
});

export const StockPageSkeleton = memo(function StockPageSkeleton() {
  return (
    <div className="max-w-4xl mx-auto pb-24 px-4">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Skeleton width="w-12" height="h-12" rounded="rounded-xl" />
            <div>
              <Skeleton width="w-32" height="h-6" className="mb-1" />
              <Skeleton width="w-24" height="h-3" />
            </div>
          </div>
          <Skeleton width="w-10" height="h-10" rounded="rounded-xl" />
        </div>
        <Skeleton width="w-full" height="h-10" rounded="rounded-xl" />
      </div>
      <SkeletonList items={3} />
    </div>
  );
});

export const RecipesPageSkeleton = memo(function RecipesPageSkeleton() {
  const categoryButtons = useMemo(() => [1, 2, 3, 4], []);
  const recipeCards = useMemo(() => [1, 2, 3, 4, 5, 6], []);

  return (
    <div className="max-w-4xl mx-auto pb-24 px-4">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Skeleton width="w-12" height="h-12" rounded="rounded-xl" />
            <div>
              <Skeleton width="w-32" height="h-6" className="mb-1" />
              <Skeleton width="w-40" height="h-3" />
            </div>
          </div>
          <Skeleton width="w-20" height="h-10" rounded="rounded-xl" />
        </div>
        <Skeleton width="w-full" height="h-10" rounded="rounded-xl" className="mb-3" />
        <div className="flex gap-2">
          {categoryButtons.map(i => (
            <Skeleton key={i} width="w-24" height="h-8" rounded="rounded-xl" />
          ))}
        </div>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {recipeCards.map(i => (
          <RecipeCardSkeleton key={i} />
        ))}
      </div>
    </div>
  );
});

const RecipeCardSkeleton = memo(function RecipeCardSkeleton() {
  return (
    <div className="bg-gray-800/40 border border-gray-700/50 rounded-xl overflow-hidden">
      <Skeleton width="w-full" height="h-28" rounded="rounded-none" />
      <div className="p-4">
        <Skeleton width="w-3/4" height="h-4" className="mb-2" />
        <Skeleton width="w-1/2" height="h-3" className="mb-3" />
        <Skeleton width="w-full" height="h-9" rounded="rounded-lg" />
      </div>
    </div>
  );
});

export const PlannerPageSkeleton = memo(function PlannerPageSkeleton() {
  const weekDays = useMemo(() => [1, 2, 3, 4, 5, 6, 7], []);

  return (
    <div className="max-w-4xl mx-auto pb-24 px-4">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Skeleton width="w-12" height="h-12" rounded="rounded-xl" />
            <div>
              <Skeleton width="w-32" height="h-6" className="mb-1" />
              <Skeleton width="w-24" height="h-3" />
            </div>
          </div>
          <Skeleton width="w-16" height="h-9" rounded="rounded-xl" />
        </div>
        <div className="bg-gray-800/50 rounded-xl p-3 flex items-center justify-between">
          <Skeleton width="w-8" height="h-8" rounded="rounded-lg" />
          <Skeleton width="w-40" height="h-4" />
          <Skeleton width="w-8" height="h-8" rounded="rounded-lg" />
        </div>
      </div>
      
      <div className="space-y-2">
        {weekDays.map(i => (
          <DayCardSkeleton key={i} />
        ))}
      </div>
    </div>
  );
});

const DayCardSkeleton = memo(function DayCardSkeleton() {
  const mealSlots = useMemo(() => [1, 2, 3, 4], []);

  return (
    <div className="bg-gray-800/40 border border-gray-700/30 rounded-xl p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <Skeleton width="w-11" height="h-12" rounded="rounded-lg" />
          <div>
            <Skeleton width="w-24" height="h-4" className="mb-1" />
            <div className="flex gap-1.5">
              {mealSlots.map(j => (
                <Skeleton key={j} width="w-1.5" height="h-1.5" rounded="rounded-full" />
              ))}
            </div>
          </div>
        </div>
        <Skeleton width="w-5" height="h-5" />
      </div>
    </div>
  );
});

export const ShoppingPageSkeleton = memo(function ShoppingPageSkeleton() {
  return (
    <div className="max-w-4xl mx-auto pb-24 px-4">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Skeleton width="w-12" height="h-12" rounded="rounded-xl" />
            <div>
              <Skeleton width="w-32" height="h-6" className="mb-1" />
              <Skeleton width="w-20" height="h-3" />
            </div>
          </div>
          <Skeleton width="w-32" height="h-9" rounded="rounded-xl" />
        </div>
        
        <div className="bg-gray-800/50 rounded-xl p-3 mb-4">
          <div className="flex justify-between mb-2">
            <Skeleton width="w-16" height="h-3" />
            <Skeleton width="w-8" height="h-3" />
          </div>
          <Skeleton width="w-full" height="h-2" />
        </div>
        
        <div className="grid grid-cols-2 gap-3 mb-4">
          <Skeleton width="w-full" height="h-20" rounded="rounded-xl" />
          <Skeleton width="w-full" height="h-20" rounded="rounded-xl" />
          <div className="col-span-2">
            <Skeleton width="w-full" height="h-20" rounded="rounded-xl" />
          </div>
        </div>
      </div>
      
      <SkeletonList items={3} />
    </div>
  );
});

// ═══════════════════════════════════════════════
// SPINNER LOADERS
// ═══════════════════════════════════════════════

export const SpinnerLoader = memo(function SpinnerLoader({ 
  size = 'md', 
  color = 'blue' 
}) {
  const sizes = useMemo(() => ({
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16'
  }), []);

  const colors = useMemo(() => ({
    blue: 'border-blue-500',
    purple: 'border-purple-500',
    green: 'border-green-500',
  }), []);

  const sizeClass = useMemo(() => sizes[size], [size, sizes]);
  const colorClass = useMemo(() => colors[color], [color, colors]);

  return (
    <div className="flex items-center justify-center min-h-[200px]">
      <div className="relative">
        <div className={`${sizeClass} rounded-full border-2 border-gray-700/20`} />
        <div className={`absolute inset-0 ${sizeClass} rounded-full border-2 ${colorClass} border-t-transparent animate-spin`} />
      </div>
    </div>
  );
});

export const DoubleSpinner = memo(function DoubleSpinner() {
  return (
    <div className="flex items-center justify-center min-h-[200px]">
      <div className="relative w-16 h-16">
        <div className="absolute inset-0 rounded-full border-2 border-purple-500/20" />
        <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-purple-500 animate-spin" />
        <div 
          className="absolute inset-2 rounded-full border-2 border-transparent border-t-pink-400 animate-spin" 
          style={{ animationDuration: '0.8s', animationDirection: 'reverse' }} 
        />
      </div>
    </div>
  );
});

// ═══════════════════════════════════════════════
// LOADING WRAPPER
// ═══════════════════════════════════════════════

export const LoadingWrapper = memo(function LoadingWrapper({ 
  loading, 
  skeleton: SkeletonComponent, 
  children 
}) {
  if (loading) {
    return SkeletonComponent ? <SkeletonComponent /> : <DoubleSpinner />;
  }
  return children;
});

// ═══════════════════════════════════════════════
// CSS EXPORT
// ═══════════════════════════════════════════════

export const SKELETON_CSS = `
@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

.animate-shimmer {
  background: linear-gradient(90deg, 
    transparent 0%, 
    rgba(99, 102, 241, 0.1) 50%, 
    transparent 100%
  );
  background-size: 200% 100%;
  animation: shimmer 2s infinite;
}
`;

export default {
  Skeleton,
  SkeletonCard,
  SkeletonList,
  SkeletonTable,
  ComponentsPageSkeleton,
  StockPageSkeleton,
  RecipesPageSkeleton,
  PlannerPageSkeleton,
  ShoppingPageSkeleton,
  SpinnerLoader,
  DoubleSpinner,
  LoadingWrapper,
  SKELETON_CSS,
};